import { hasMissionLinkInTree } from '../../../../../utils/nestedCampaignInvariant';

/**
 * Sort nested sprints so parents are persisted before children (POST order).
 */
export function sortNestedSprintsForPersistence(nestedSprints, rootParentId) {
  if (!nestedSprints?.length) return [];
  const byId = new Map(nestedSprints.map(s => [s.id, s]));

  const depthOf = (sprint) => {
    let depth = 0;
    let pid = sprint.parentSprintId;
    const seen = new Set();
    while (pid && pid !== rootParentId && !seen.has(pid)) {
      seen.add(pid);
      depth++;
      const parent = byId.get(pid);
      if (!parent) break;
      pid = parent.parentSprintId;
    }
    return depth;
  };

  return [...nestedSprints].sort((a, b) => depthOf(a) - depthOf(b));
}

/**
 * Flush the parent (with mission links) first, then persist only nested
 * campaigns that a mission actually contains.
 */
export async function persistPastedNestedSprints({
  nestedSprints,
  rootParentId,
  onAddSprint,
  onUpdateSprint,
  parentSprint,
}) {
  // Parent mission links must land in global state BEFORE nested POSTs, so a
  // nested campaign is never persisted without a mission container.
  if (onUpdateSprint && parentSprint) {
    onUpdateSprint(parentSprint);
  }

  if (nestedSprints?.length && onAddSprint) {
    const linked = nestedSprints.filter((nestedSprint) =>
      hasMissionLinkInTree(nestedSprint?.id, parentSprint, nestedSprints)
    );
    const dropped = nestedSprints.length - linked.length;
    if (dropped > 0) {
      console.error(
        `[NEST INVARIANT] Refusing to persist ${dropped} nested campaign(s) with no mission container`,
        nestedSprints
          .filter((s) => !hasMissionLinkInTree(s?.id, parentSprint, nestedSprints))
          .map((s) => ({ id: s.id, name: s.name, parentSprintId: s.parentSprintId }))
      );
    }

    const sorted = sortNestedSprintsForPersistence(linked, rootParentId);
    const seenIds = new Set();
    for (const nestedSprint of sorted) {
      if (!nestedSprint?.id || seenIds.has(nestedSprint.id)) continue;
      seenIds.add(nestedSprint.id);
      await onAddSprint({
        ...nestedSprint,
        parentSprintId: nestedSprint.parentSprintId ?? rootParentId,
      });
    }
  }
}
